import { action, observable, computed, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  type JobScopeChangeStore,
  type AdminJobScopeUpdateCustomItemToCreateParams,
  type AdminJobScopeUpdateCustomItemToRemoveParams,
  type AdminJobScopeUpdateInventoryItemParams,
} from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { validationContext } from 'validx'
import { type ListInventoryItemsFn } from '@/features/inventory'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { Id } from '@/infra/id'
import { type Job, type JobScopeItem } from '@/features/jobs/Job'
import { ManageItemsViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ManageItems/ManageItemsViewModel'
import { mustHaveMaxLength } from '@/utils/validx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { JobScopeCustomItemViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeCustomItem/JobScopeCustomItemViewModel'
import { JobScopeInventoryItemViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/ListItems/JobScopeInventoryItem/JobScopeInventoryItemViewModel'

/**
 * A custom service already on the job that is removed from its scope.
 */
export interface CustomItemToRemove {
  id: Id
}

/**
 * Describes the state of the view model.
 */
export type UpdateJobScopeState =
  /**
   * Admin can update the current job scope or add more items.
   */
  | 'UpdateJobScope'
  /**
   * Admin can specify the reason for the job scope update.
   */
  | 'SpecifyReason'
  /**
   * Collaborators on the job will be notified of the changes.
   */
  | 'Success'
  /**
   * Something went wrong while executing the update job scope command, could be a
   * validation error or something transient.
   */
  | 'Failed'

/**
 * View Model for updating the job scope as an admin.
 */
export class UpdateJobScopeDialogViewModel implements ActivationLifecycle {
  /**
   * The maximum length of the reason.
   */
  static readonly maxReasonLength = 280

  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Adds and removes services.
   */
  readonly manageItemsVm

  /**
   * Validation context for the reason.
   */
  readonly validation

  /**
   * The step the dialog is on.
   */
  @observable
  accessor state: UpdateJobScopeState = 'UpdateJobScope'

  /**
   * The message of the failed update.
   */
  @observable
  accessor error = ''

  /**
   * Why the scope is updated.
   */
  @observable
  accessor reason = ''

  /**
   * The services the updated scope holds.
   */
  @observable
  accessor selectedInventoryItems: JobScopeInventoryItemViewModel[] = []

  /**
   * The custom services the updated scope holds.
   */
  @observable
  accessor selectedCustomItems: JobScopeCustomItemViewModel[] = []

  /**
   * The custom services already on the job that are removed.
   */
  @observable
  accessor selectedCustomItemIdsToRemove: CustomItemToRemove[] = []

  constructor(
    private readonly _job: Job,
    private readonly _searchServiceItems: ListInventoryItemsFn,
    private readonly _workspaceId: Id,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.manageItemsVm = new ManageItemsViewModel({
      job: this._job,
      workspaceId: this._workspaceId,
      searchServiceItems: this._searchServiceItems,
      createAlertRetryWrapper: this._createAlertRetryWrapper,
      onAddInventoryItem: this.addInventoryItem,
      onSetInventoryItems: this.setSelectedInventoryItems,
      onSetCustomItems: this.setSelectedCustomItems,
      onRemoveCustomItem: this.addCustomItemToBeRemoved,
      onReset: this._syncManageItemsVm,
    })

    this.validation = validationContext(this, {
      reason: [
        mustHaveMaxLength(
          UpdateJobScopeDialogViewModel.maxReasonLength,
          `The reason can't be longer than ${UpdateJobScopeDialogViewModel.maxReasonLength} characters.`,
        ),
      ],
    })

    this._lifecycle.manage(this.manageItemsVm)
  }

  /**
   * Selected inventory items that haven't been removed from the job scope.
   */
  @computed
  get visibleInventoryItems() {
    return this.selectedInventoryItems.filter((i) => i.canShow)
  }

  /**
   * Whether the updated scope holds no services.
   */
  @computed
  get isNoServiceItemsOnJob() {
    return (
      this.selectedCustomItems.length === 0 &&
      this.visibleInventoryItems.length === 0
    )
  }

  /**
   * Whether the scope is unchanged or invalid.
   */
  @computed
  get disabled() {
    const customItemsIsNotValid =
      this.selectedCustomItems.filter((item) => item.newQuantity < 0).length > 0
    const inventoryItemsIsNotValid =
      this.selectedInventoryItems.filter((item) => item.newQuantity < 0)
        .length > 0

    if (customItemsIsNotValid || inventoryItemsIsNotValid) {
      return true
    }

    const containsAddedCustomItems =
      this.selectedCustomItems.filter((item) => item.source === 'Added')
        .length > 0

    const isCustomItemQuantityChanged =
      this.selectedCustomItems.filter((i) => i.source === 'Job').length !==
      this._job.jobScopeItems.filter((i) => i.customItemId !== null).length

    const isJobScopeInventoryItemsEqualToSelectedInventoryItems =
      this._isEqualItemsFromJobScope(
        this._job.jobScopeItems.filter((i) => i.inventoryItemVersionId),
        this.selectedInventoryItems,
      )

    return containsAddedCustomItems || isCustomItemQuantityChanged
      ? false
      : isJobScopeInventoryItemsEqualToSelectedInventoryItems
  }

  /**
   * Reason text box characters.
   */
  @computed
  get reasonCharsRemaining() {
    if (this.reason) {
      return UpdateJobScopeDialogViewModel.maxReasonLength - this.reason.length
    }

    return UpdateJobScopeDialogViewModel.maxReasonLength
  }

  /**
   * Update the job scope.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const customItemsToAdd: AdminJobScopeUpdateCustomItemToCreateParams[] =
      this.selectedCustomItems
        .filter((item) => item.source === 'Added')
        .map((item) => ({
          name: item.name,
          quantity: item.newQuantity,
          price: item.price,
          proPrice: item.proPrice,
        }))

    const customItemsToRemove: AdminJobScopeUpdateCustomItemToRemoveParams[] =
      this.selectedCustomItemIdsToRemove.map((item) => ({
        customItemId: item.id,
      }))

    // Only change the job scope for the inventory items that have a different new quantity
    const inventoryItems: AdminJobScopeUpdateInventoryItemParams[] =
      this.selectedInventoryItems
        .filter((item) => item.newQuantity !== item.currentQuantity)
        .map((item) => ({
          versionId: item.versionId,
          quantity: item.newQuantity,
        }))

    try {
      await this._jobScopeChangeStore.updateJobScopeAsAdmin({
        jobId: this._job.id,
        reason: this.reason,
        scopeChangeCustomItemsToCreate: customItemsToAdd,
        scopeChangeCustomItemsToRemove: customItemsToRemove,
        scopeChangeInventoryItems: inventoryItems,
      })
      this.setState('Success')
    } catch (err) {
      runInAction(() => {
        this.error = (err as Error).message
        this.setState('Failed')
      })
    }
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Indicates if the reason is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Resets the state.
   */
  @action.bound
  reset() {
    this.submit.reset()
    this.setState('UpdateJobScope')

    this.reason = ''
    this.error = ''
    this.selectedInventoryItems = []
    this.selectedCustomItems = []
    this.selectedCustomItemIdsToRemove = []
    this.validation.reset()
  }

  /**
   * Shows the dialog with the job's current scope.
   */
  @action.bound
  show() {
    this._addJobScopeItemsFromJob()
    this.modal.show()
  }

  /**
   * Sets the step the dialog is on.
   */
  @action.bound
  setState(state: UpdateJobScopeState) {
    this.state = state
  }

  /**
   * Moves on to the reason.
   */
  @action.bound
  moveToReasonStep() {
    this.setState('SpecifyReason')
  }

  /**
   * Goes back to the services.
   */
  @action.bound
  backToItemsStep() {
    this.setState('UpdateJobScope')
  }

  /**
   * Sets the custom services the updated scope holds.
   */
  @action.bound
  setSelectedCustomItems(items: JobScopeCustomItemViewModel[]) {
    this.selectedCustomItems = items
  }

  /**
   * Marks a custom service already on the job as removed.
   */
  @action.bound
  addCustomItemToBeRemoved(id: Id) {
    this.selectedCustomItemIdsToRemove.push({ id })
  }

  /**
   * Adds a custom service.
   */
  @action.bound
  addCustomItem(customItem: JobScopeCustomItemViewModel) {
    this.selectedCustomItems.push(customItem)
  }

  /**
   * Adds a service.
   */
  @action.bound
  addInventoryItem(inventoryItem: JobScopeInventoryItemViewModel) {
    this.selectedInventoryItems.push(inventoryItem)
  }

  /**
   * Sets the services the updated scope holds.
   */
  @action.bound
  setSelectedInventoryItems(inventoryItems: JobScopeInventoryItemViewModel[]) {
    this.selectedInventoryItems = inventoryItems
  }

  /**
   * Removes a custom service.
   */
  @action.bound
  removeCustomItem(id: Id) {
    const customItem = this.selectedCustomItems.find((item) => item.id === id)
    if (customItem?.source === 'Job') {
      this.addCustomItemToBeRemoved(id)
    }

    this.selectedCustomItems = this.selectedCustomItems.filter(
      (item) => item.id !== id,
    )
    this.manageItemsVm.setSelectedCustomItems(this.selectedCustomItems)
  }

  @action.bound
  private _addJobScopeItemsFromJob() {
    this._job.jobScopeItems.forEach((item) => {
      // We don't want to show the bundle in this list.
      if (item.bundleId !== null) {
        return
      }

      if (item.customItemId !== null) {
        const customItem = new JobScopeCustomItemViewModel('Job')
        customItem.setId(item.customItemId)
        customItem.setName(item.name)
        customItem.setCurrentQuantity(item.quantity)
        customItem.setPrice(item.price ?? 0)
        customItem.setProPrice(item.proPrice ?? 0)
        this.addCustomItem(customItem)
        return
      }

      const inventoryItem = new JobScopeInventoryItemViewModel(item, (i) =>
        i.removeFromJobScope(),
      )

      this.addInventoryItem(inventoryItem)
    })

    this.manageItemsVm.setSelectedInventoryItems(
      this.selectedInventoryItems.slice(),
    )
    this.manageItemsVm.setSelectedCustomItems(this.selectedCustomItems.slice())
  }

  @action.bound
  private _syncManageItemsVm() {
    this.manageItemsVm.setSelectedInventoryItems(
      this.selectedInventoryItems.slice(),
    )
    this.setSelectedCustomItems(
      this.selectedCustomItems.filter((item) => item.name !== ''),
    )
    this.manageItemsVm.setSelectedCustomItems(this.selectedCustomItems.slice())
  }

  private _isEqualItemsFromJobScope(
    jobScopeInventoryItems: JobScopeItem[],
    selectedInventoryItems: JobScopeInventoryItemViewModel[],
  ): boolean {
    if (jobScopeInventoryItems.length !== selectedInventoryItems.length) {
      return false
    }

    return jobScopeInventoryItems.every((item1) => {
      const foundItem = selectedInventoryItems.find(
        (i) => i.versionId === item1.inventoryItemVersionId,
      )
      if (!foundItem) {
        return false
      }

      return (
        foundItem.id === item1.inventoryItemId &&
        foundItem.versionId === item1.inventoryItemVersionId &&
        foundItem.newQuantity === item1.quantity
      )
    })
  }
}
